import auth0, { Auth0Error } from 'auth0-js'
import {
  LoginError,
  ValidateSignUpEmail,
  ValidateCode,
  ResendCode,
  Login,
  CreateNewPassword,
  ValidateSignUpEmailError,
  ValidateCodeError,
  ResendCodeError,
  CreateNewPasswordError,
  SocialLogin,
} from '.'
import { createSuccessResult, createErrorResult } from '../types/result'
import { Cookies, CookieName } from '../cookies'

export enum UserFlow {
  LOGIN = 'LOGIN',
  LOGIN_GOOGLE = 'LOGIN_GOOGLE',
  LOGIN_APPLE = 'LOGIN_APPLE',
  SIGN_UP = 'SIGN_UP',
  SIGN_UP_GOOGLE = 'SIGN_UP_GOOGLE',
  SIGN_UP_APPLE = 'SIGN_UP_APPLE',
  CREATE_PASSWORD = 'CREATE_PASSWORD',
}

const { auth0Config } = window
const flowCookieName = CookieName.UniversalLoginFlow

const webAuth = new auth0.WebAuth(
  Object.assign(
    {
      overrides: {
        __tenant: auth0Config.auth0Tenant,
        __token_issuer: auth0Config.authorizationServer.issuer,
      },
      domain: auth0Config.auth0Domain,
      clientID: auth0Config.clientID,
      redirectUri: auth0Config.callbackURL,
      responseType:
        auth0Config.extraParams.response_type ||
        auth0Config.internalOptions.response_type,
    },
    auth0Config.internalOptions
  )
)

export const validateSignUpEmail: ValidateSignUpEmail = async (email) => {
  return new Promise((resolve, reject) => {
    webAuth.passwordlessStart(
      {
        connection: 'email',
        send: 'code',
        email,
      },
      function (err) {
        if (!err) {
          resolve(createSuccessResult())
          return
        }

        const errors = []

        switch (err.code) {
          case 'bad.email':
            errors.push(ValidateSignUpEmailError.Invalid)
            break
          default:
            reject(
              Error(
                `Code request failed due to a request error while talking to Auth0 (eg. network issue)`
              )
            )
            return
        }

        resolve(createErrorResult(errors))
      }
    )
  })
}

export const validateCode: ValidateCode = async (
  email,
  verificationCode,
  actionName,
  cookieDomain
) => {
  const userFlow =
    actionName === 'create-password'
      ? UserFlow.CREATE_PASSWORD
      : UserFlow.SIGN_UP

  Cookies.set(flowCookieName, userFlow, {
    domain: cookieDomain,
  })

  return new Promise((resolve, reject) => {
    // The errors are different when running locally vs in Auth0's Universal Login
    // This is due to automatic detection that happens in auth0.js
    // See: https://github.com/auth0/auth0.js/blob/v9.13.4/src/web-auth/index.js#L781-L789
    webAuth.passwordlessLogin(
      {
        connection: 'email',
        email,
        verificationCode,
      },
      function (err) {
        if (!err) {
          resolve(createSuccessResult())
          return
        }

        const errors = []

        // Auth0 is inconsistent in regards to errors (see Auth0Error interface for details)
        // @see https://auth0.com/docs/libraries/common-auth0-library-authentication-errors
        switch (err.code) {
          case 'access_denied': // when running locally
          // falls through
          case 'invalid_user_password': // in Universal Login app
            errors.push(ValidateCodeError.Mismatch)
            break
          case 'too_many_attempts':
            errors.push(ValidateCodeError.TooManyAttempts)
            break
          default:
            reject(
              Error(
                `Login failed due to a request error while talking to Auth0 (eg. network issue, server down)`
              )
            )
            return
        }

        resolve(createErrorResult(errors))
      }
    )
  })
}

export const resendCode: ResendCode = async (email) => {
  const res = await validateSignUpEmail(email)

  return res.success
    ? createSuccessResult()
    : createErrorResult([ResendCodeError.Uncertain])
}

export const authorize: SocialLogin = ({
  userFlow,
  connection,
  cookieDomain,
}) => {
  Cookies.set(flowCookieName, userFlow, {
    domain: cookieDomain,
  })
  webAuth.authorize({
    connection,
    scope: 'openid profile email',
    prompt: 'login',
  })
}
export const login: Login = (email, password, cookieDomain) => {
  Cookies.set(flowCookieName, UserFlow.LOGIN, {
    domain: cookieDomain,
  })

  return new Promise((resolve, reject) => {
    // The errors are different when running locally vs in Auth0's Universal Login
    // This is due to automatic detection that happens in auth0.js
    // See: https://github.com/auth0/auth0.js/blob/v9.13.4/src/web-auth/index.js#L781-L789
    webAuth.login(
      {
        realm: auth0Config.connection,
        username: email,
        password,
      },
      (err: Auth0Error | null) => {
        if (!err) {
          resolve(createSuccessResult())
          return
        }

        const errors = []

        // Auth0 is inconsistent in regards to errors (see Auth0Error interface for details)
        // @see https://auth0.com/docs/libraries/common-auth0-library-authentication-errors
        switch (err.code) {
          case 'access_denied': // when running locally
          // falls through
          case 'invalid_user_password': // in Universal Login app
            errors.push(LoginError.InvalidCredentials)
            break
          case 'password_leaked':
            errors.push(LoginError.PasswordLeaked)
            break
          case 'blocked_user': // when running locally
          // falls through
          case 'unauthorized': // in Universal Login app
            errors.push(LoginError.BlockedUser)
            break
          case 'too_many_attempts':
            errors.push(LoginError.TooManyAttempts)
            break
          default:
            reject(
              Error(
                `Login failed due to a request error while talking to Auth0 (eg. network issue)`
              )
            )
            return
        }

        resolve(createErrorResult(errors))
      }
    )
  })
}

export const createNewPassword: CreateNewPassword = async (email) => {
  return new Promise((resolve, reject) => {
    webAuth.passwordlessStart(
      {
        connection: 'email',
        send: 'code',
        email,
      },
      function (err) {
        if (!err) {
          resolve(createSuccessResult())
          return
        }

        const errors = []

        switch (err.code) {
          case 'bad.email':
            errors.push(CreateNewPasswordError.Invalid)
            break
          default:
            reject(
              Error(
                `Code request failed due to a request error while talking to Auth0 (eg. network issue)`
              )
            )
            return
        }

        resolve(createErrorResult(errors))
      }
    )
  })
}
